/* eslint-disable no-console -- opt-in debug logging via localStorage */
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { StowedElement } from '@/api/types/stowed-element'

/** Set `localStorage.setItem('DEBUG_SHAPE_GEOMETRY', '1')` and reload to enable logs. */
export function isShapeGeometryDebug(): boolean {
  if (typeof window === 'undefined') return false

  try {
    return window.localStorage.getItem('DEBUG_SHAPE_GEOMETRY') === '1'
  } catch {
    return false
  }
}

export function shapeGeometrySummary(shape: ShapeData): Record<string, unknown> {
  return {
    id: shape.id,
    kindId: shape.kindId,
    parentGroupId: shape.parentGroupId,
    type: shape.type,
    x: shape.x,
    y: shape.y,
    width: shape.width,
    height: shape.height,
    rotation: shape.rotation,
    locked: shape.locked,
    fractionalIndex: shape.fractionalIndex,
  }
}

function pickBase(stowed: StowedElement): unknown {
  const k = stowed.kindId

  if (k === 7) return stowed.groupElement?.base
  if (k === 2) return stowed.irregularPolygonElement?.base
  if (k === 10) return stowed.regularPolygonElement?.base
  if (k === 1) return stowed.rectangleElement?.base

  return undefined
}

/** Compact view of rotate/move/resize batch bodies (full stowed rows can be huge). */
export function stowedBatchGeometrySummary(batch: StowedElement[]): unknown[] {
  return batch.map((row) => ({
    objectId: row.objectId,
    kindId: row.kindId,
    fractionalIndex: row.fractionalIndex,
    base: pickBase(row),
  }))
}

export function logGroupRotateDebug(args: {
  label: 'committed' | 'batch'
  group: ShapeData
  descendants: ShapeData[]
  stowedBatch: StowedElement[]
}): void {
  if (!isShapeGeometryDebug()) return

  const { label, group, descendants, stowedBatch } = args

  console.log(`[group-rotate] (${label}) frontend kind-7 ShapeData:`, shapeGeometrySummary(group))
  console.log(
    `[group-rotate] (${label}) frontend descendants under group (canvas-absolute):`,
    descendants.map(shapeGeometrySummary)
  )
  console.log(
    '[group-rotate] rotate API body geometry summary:',
    stowedBatchGeometrySummary(stowedBatch)
  )
  console.log('[group-rotate] rotate API body (full stowedBatch):', stowedBatch)
}

export function logCheckpointKind7FromWire(args: {
  objectId: string
  wrapperElement: Record<string, unknown>
  decodedData: Record<string, unknown>
  childWrappers: unknown[]
  reconstructedGroup: ShapeData
}): void {
  if (!isShapeGeometryDebug()) return

  const { objectId, wrapperElement, decodedData, childWrappers, reconstructedGroup } = args

  console.log('[checkpoint-load] kind-7 checkpoint wire (wrapper + decodedData + child refs):', {
    objectId,
    wrapperElement,
    decodedData,
    childWrappers,
  })
  console.log(
    '[checkpoint-load] kind-7 reconstructed ShapeData (group row):',
    shapeGeometrySummary(reconstructedGroup)
  )
}

export function logCheckpointChildUnderGroup(parentGroupId: string, shape: ShapeData): void {
  if (!isShapeGeometryDebug()) return

  console.log(
    `[checkpoint-load] reconstructed non-group under parent "${parentGroupId}":`,
    shapeGeometrySummary(shape)
  )
}
